/*
 * Pure view-model for the My Leads / Team's Leads tabs (PROD-9977): period, refresh timing,
 * IST formatting, label maps for API kinds, per-level tiles and sections, shift chart.
 * `now` is always a parameter so tests pin the clock.
 */
import { fmtTime } from '../session_allocation/allocation';
import { LEVELS } from '../level';
import { OUTCOMES } from '../outcome';
import type { Tone } from '../ui';
import type { RepLevel, TeamMember } from '../../sales_team/api';
import type {
  JoinedLead,
  LeadsPeriod,
  LeadsShift,
  LevelShiftSummary,
  MedianCounts,
  Scorecard,
  MyLeadsData,
  NowWith,
  Signal,
  Touch,
  TrialStatus
} from './types';

const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;
// IST has no DST.
const IST_OFFSET_MS = 330 * MINUTE_MS;

export const PERIODS: LeadsPeriod[] = [7, 14, 21, 35];
export const DEFAULT_PERIOD: LeadsPeriod = 7;
// Server cache TTL; also the poll interval when the server's interval is unknown.
export const POLL_FALLBACK_MS = 5 * MINUTE_MS;
// Poll just after the server cache expires, never in a tight loop.
export const REFRESH_GRACE_MS = 2000;
export const MIN_POLL_MS = 15 * 1000;
// A touch older than this reads as "No touch for N days".
const STALE_TOUCH_DAYS = 7;

export type CellTone = 'plain' | 'muted' | 'success' | 'warning' | 'error' | 'info';
export interface Cell {
  text: string;
  tone: CellTone;
}

const toMs = (v: string | number | null | undefined): number => {
  if (typeof v === 'number') return v;
  if (!v) return NaN;
  return Date.parse(v);
};

const plural = (n: number, one: string, many: string): string => `${n} ${n === 1 ? one : many}`;

// One decimal at most: 2.1, 3.
const fmtNum = (v: number): string => String(Math.round(v * 10) / 10);

const humanize = (code: string): string => {
  const s = code.replace(/_/g, ' ').toLowerCase().trim();
  return s ? s[0].toUpperCase() + s.slice(1) : '';
};

// Level chip tone, shared by the page header and the Team's Leads picker.
export const LEVEL_TONE: Record<RepLevel, Tone> = { L1: 'info', L2: 'brand', L3: 'warning' };

export const parsePeriod = (v: unknown): LeadsPeriod => {
  const n = Number(v);
  return (PERIODS as number[]).includes(n) ? (n as LeadsPeriod) : DEFAULT_PERIOD;
};

export const periodLabel = (p: LeadsPeriod): string => `Last ${p} days`;

// ---- refresh timing ----

type Stamp = string | null | undefined;

// How long to wait from arrival, all on the server's clock (skew-free): what is left of the cache entry
// (next_refresh_at - server_time), else the full generated_at interval for older payloads. null = unknown.
const serverWaitMs = (
  generatedAt: Stamp,
  nextRefreshAt: Stamp,
  serverTime: Stamp
): number | null => {
  const next = toMs(nextRefreshAt);
  if (Number.isNaN(next)) return null;
  const served = toMs(serverTime);
  // May be <= 0 for an entry at its expiry; clampPoll floors that at MIN_POLL_MS.
  if (!Number.isNaN(served)) return next - served;
  const iv = next - toMs(generatedAt);
  return Number.isNaN(iv) || iv <= 0 ? null : iv;
};

const clampPoll = (ms: number): number =>
  Math.min(Math.max(ms, MIN_POLL_MS), POLL_FALLBACK_MS + REFRESH_GRACE_MS);

// Client time of the next poll: the server's remaining wait counted from when the response arrived.
const pollDueAt = (
  generatedAt: Stamp,
  nextRefreshAt: Stamp,
  arrivedAt: number,
  serverTime: Stamp
): number => {
  const wait = serverWaitMs(generatedAt, nextRefreshAt, serverTime);
  return arrivedAt + (wait === null ? POLL_FALLBACK_MS : clampPoll(wait + REFRESH_GRACE_MS));
};

export const nextPollDelay = (
  generatedAt: Stamp,
  nextRefreshAt: Stamp,
  arrivedAt: number,
  now: number,
  serverTime?: Stamp
): number => {
  if (serverWaitMs(generatedAt, nextRefreshAt, serverTime) === null) return POLL_FALLBACK_MS;
  return clampPoll(pollDueAt(generatedAt, nextRefreshAt, arrivedAt, serverTime) - now);
};

// Client time the server cache expires, for the countdown: the poll due time less the grace.
export const refreshDueAt = (
  generatedAt: Stamp,
  nextRefreshAt: Stamp,
  arrivedAt: number,
  serverTime?: Stamp
): number | null => {
  if (serverWaitMs(generatedAt, nextRefreshAt, serverTime) === null) return null;
  return pollDueAt(generatedAt, nextRefreshAt, arrivedAt, serverTime) - REFRESH_GRACE_MS;
};

// Tab-return catch-up: only at the same due time as the timer, and never within MIN_POLL_MS
// of the last load.
export const shouldCatchUp = (
  generatedAt: Stamp,
  nextRefreshAt: Stamp,
  arrivedAt: number,
  lastLoadAt: number,
  now: number,
  missedPoll: boolean,
  serverTime?: Stamp
): boolean => {
  if (now - lastLoadAt < MIN_POLL_MS) return false;
  if (missedPoll) return true;
  return now >= pollDueAt(generatedAt, nextRefreshAt, arrivedAt, serverTime);
};

export const countdownLabel = (dueAt: number | null, now: number): string => {
  if (dueAt === null) return '';
  const left = dueAt - now;
  if (left <= 0) return 'Refreshing…';
  const secs = Math.ceil(left / 1000);
  return `Next refresh in ${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, '0')}`;
};

// ---- IST formatting ----

export const fmtDay = (at: string | number | null | undefined): string => {
  const t = toMs(at);
  if (Number.isNaN(t)) return '—';
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Kolkata',
    day: 'numeric',
    month: 'short'
  }).formatToParts(new Date(t));
  const get = (type: string) => parts.find((p) => p.type === type)?.value || '';
  return `${get('day')} ${get('month')}`;
};

// "29 Oct to 4 Nov" from the payload's IST dates (YYYY-MM-DD); '' when unreadable.
export const periodRange = (
  startIst: string | null | undefined,
  endIst: string | null | undefined
): string => {
  const day = (v: string | null | undefined) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v || '');
    return m ? fmtDay(`${m[1]}-${m[2]}-${m[3]}T12:00:00+05:30`) : '—';
  };
  const from = day(startIst);
  const to = day(endIst);
  return from === '—' || to === '—' ? '' : `${from} to ${to}`;
};

export const fmtDayTime = (at: string | number | null | undefined): string => {
  const day = fmtDay(at);
  return day === '—' ? day : `${day}, ${fmtTime(at)}`;
};

export const updatedLabel = (generatedAt: string | null | undefined): string => {
  const time = fmtTime(generatedAt);
  return time ? `Updated ${time} IST` : '';
};

const istDay = (t: number): number => Math.floor((t + IST_OFFSET_MS) / DAY_MS);

const istDaysSince = (at: string | null | undefined, now: number): number => {
  const t = toMs(at);
  if (Number.isNaN(t)) return 0;
  return Math.max(0, istDay(now) - istDay(t));
};

export const agoLabel = (at: string | null | undefined, now: number): string => {
  const n = istDaysSince(at, now);
  if (n === 0) return 'today';
  if (n === 1) return 'yesterday';
  return `${n} days ago`;
};

// ---- date groups for the row tables ----

export interface DateGroup<T> {
  // IST day number (days since epoch); null for rows with an unreadable date.
  day: number | null;
  rows: T[];
}

// Newest day first; the unreadable-date group (day null) last.
const byDayDesc = (a: number | null, b: number | null): number => {
  if (a === b) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return b - a;
};

// Groups rows by IST calendar day, newest day first, and rows within a day by the same timestamp,
// newest first (unreadable dates keep list order, last). Sorted: L1 rows arrive by booked-on.
export const groupRowsByIstDate = <T>(
  rows: T[],
  getIso: (row: T) => string | null | undefined
): DateGroup<T>[] => {
  const byDay = new Map<number | null, Array<{ row: T; t: number; i: number }>>();
  rows.forEach((row, i) => {
    const t = toMs(getIso(row));
    const day = Number.isNaN(t) ? null : istDay(t);
    const list = byDay.get(day) || [];
    list.push({ row, t: Number.isNaN(t) ? 0 : t, i });
    byDay.set(day, list);
  });
  return Array.from(byDay.keys())
    .sort(byDayDesc)
    .map((day) => ({
      day,
      rows: (byDay.get(day) || []).sort((x, y) => y.t - x.t || x.i - y.i).map((x) => x.row)
    }));
};

// "Today" / "Yesterday" / "Tomorrow", else "Mon, 3 Nov" (IST).
export const dayHeading = (day: number | null, now: number): string => {
  if (day === null) return 'No date';
  const diff = istDay(now) - day;
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Yesterday';
  if (diff === -1) return 'Tomorrow';
  // Noon of that IST day, so the formatter cannot slip a day.
  const noon = new Date(day * DAY_MS - IST_OFFSET_MS + 12 * HOUR_MS);
  const weekday = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Kolkata',
    weekday: 'short'
  }).format(noon);
  return `${weekday}, ${fmtDay(noon.getTime())}`;
};

export type GroupNoun = 'trial' | 'lead' | 'member';

export const groupCountLabel = (n: number, noun: GroupNoun): string => plural(n, noun, `${noun}s`);

// Phone day heading (no column header there to say which date): "Paid on Mon, 3 Nov",
// "Paid today", "Joined Mon, 3 Nov", "Joined yesterday"; trial tables stay plain.
export type HeadingVerb = 'paid' | 'joined';
export const phoneDayHeading = (heading: string, verb?: HeadingVerb): string => {
  if (!verb) return heading;
  const relative = ['Today', 'Yesterday', 'Tomorrow', 'No date'].includes(heading);
  const lead = verb === 'paid' ? 'Paid' : 'Joined';
  if (relative) return heading === 'No date' ? heading : `${lead} ${heading.toLowerCase()}`;
  return verb === 'paid' ? `Paid on ${heading}` : `Joined ${heading}`;
};

// ---- label maps for API kinds ----

const touchKindLabel = (touch: Touch): string => {
  switch (touch.kind) {
    case 'call':
      return 'Call';
    case 'call_no_answer':
      return 'Call, no answer';
    case 'sms':
      return 'SMS';
    case 'whatsapp':
      return 'WhatsApp';
    case 'note':
      return 'Note';
    case 'task': {
      if (!touch.outcome) return 'Task done';
      const outcome =
        OUTCOMES.find((o) => o.code === touch.outcome)?.label || humanize(touch.outcome);
      return `Task done (${outcome})`;
    }
    default:
      return humanize(String(touch.kind));
  }
};

const SIGNAL_LABELS: Record<string, string> = {
  three_month_plan_opened: '3-month plan opened',
  pricing_page_visited: 'Pricing page visit',
  booked_trial: 'Booked another trial',
  session_joined: 'Joined a session',
  session_finished: 'Finished a session',
  first_pc_booked: 'Booked first 1-on-1',
  trial_reset: 'Trial reset'
};

export const lastTouchCell = (
  touch: Touch | null | undefined,
  joinedAt: string | null | undefined,
  now: number
): Cell => {
  if (!touch) {
    const n = istDaysSince(joinedAt, now);
    return {
      text: n >= 1 ? `No touch for ${plural(n, 'day', 'days')}` : 'No touch yet',
      tone: 'warning'
    };
  }
  const age = istDaysSince(touch.at, now);
  if (age >= STALE_TOUCH_DAYS) return { text: `No touch for ${age} days`, tone: 'warning' };
  return { text: `${touchKindLabel(touch)}, ${agoLabel(touch.at, now)}`, tone: 'plain' };
};

export const touchBeforeLabel = (
  touch: Touch | null | undefined,
  startAt: string | null | undefined
): Cell => {
  if (!touch) return { text: 'No touch before', tone: 'warning' };
  const gap = Math.max(0, toMs(startAt) - toMs(touch.at));
  let when = '';
  if (Number.isNaN(gap)) when = '';
  else if (gap < HOUR_MS) when = `${Math.round(gap / MINUTE_MS)} min before`;
  else if (gap < DAY_MS) when = `${Math.round(gap / HOUR_MS)} h before`;
  else when = `${Math.round(gap / DAY_MS)} d before`;
  return {
    text: when ? `${touchKindLabel(touch)}, ${when}` : touchKindLabel(touch),
    tone: 'plain'
  };
};

export const signalCell = (signal: Signal | null | undefined, now: number): Cell => {
  if (!signal) return { text: '—', tone: 'muted' };
  const label = SIGNAL_LABELS[signal.kind] || humanize(String(signal.kind));
  return { text: `${label}, ${agoLabel(signal.at, now)}`, tone: 'plain' };
};

export const daysLeftCell = (n: number): Cell => {
  if (n <= 0) return { text: 'Ends today', tone: 'error' };
  let tone: CellTone = 'plain';
  if (n <= 3) tone = 'error';
  else if (n <= 7) tone = 'warning';
  return { text: `${n} d`, tone };
};

// Leads I joined status: "Open · 3 d left", "Paid · 30 Oct", "Window ended".
export const leadStatusCell = (row: Pick<JoinedLead, 'status' | 'days_left' | 'paid_at'>): Cell => {
  if (row.status === 'paid') return { text: `Paid · ${fmtDay(row.paid_at)}`, tone: 'success' };
  if (row.status === 'window_ended') return { text: 'Window ended', tone: 'muted' };
  const left = daysLeftCell(row.days_left);
  return {
    text: row.days_left <= 0 ? 'Open · ends today' : `Open · ${row.days_left} d left`,
    tone: left.tone
  };
};

const STATUS_CELLS: Record<TrialStatus, Cell> = {
  attended: { text: 'Attended', tone: 'success' },
  no_show: { text: 'No-show', tone: 'error' },
  cancelled: { text: 'Cancelled', tone: 'muted' },
  upcoming: { text: 'Upcoming', tone: 'info' },
  unknown: { text: 'Under review', tone: 'muted' }
};

// L2's column asks "Joined?", so attended reads as Joined there.
export const trialStatusCell = (status: TrialStatus, level: RepLevel): Cell => {
  if (status === 'attended' && level === 'L2') return { text: 'Joined', tone: 'success' };
  return STATUS_CELLS[status] || { text: humanize(String(status)), tone: 'plain' };
};

export const viaLabel = (via: string): string =>
  via === 'after_task' ? 'After my task' : 'Booked by me';

export const nowWithLabel = (nw: NowWith | null | undefined): string => {
  if (nw?.label) return nw.label;
  if (nw?.kind === 'advisor') return 'Advisor';
  if (nw?.kind === 'task') return 'Open task';
  return 'No one yet';
};

// Mirrors the server mapping; 28 weeks is the 6-month package too.
export const planLabel = (
  weeks: number | null | undefined,
  label: string | null | undefined
): string => {
  if (label) return label;
  if (weeks === 12) return '3 months';
  if (weeks === 24 || weeks === 28) return '6 months';
  if (weeks === 52) return '12 months';
  return 'Monthly';
};

export const daysToPayCell = (n: number | null | undefined): Cell => {
  if (n === null || n === undefined) return { text: '—', tone: 'muted' };
  return { text: fmtNum(n), tone: n <= 2 ? 'success' : 'plain' };
};

// Rates are fractions; a value above 1 is taken as already a percent.
export const pct = (rate: number | null | undefined): string => {
  if (rate === null || rate === undefined || Number.isNaN(rate)) return '—';
  return `${Math.round(rate > 1 ? rate : rate * 100)}%`;
};

const joinHint = (...parts: string[]): string => parts.filter(Boolean).join(' · ');

const ending = (n: number): string => (n > 0 ? `${n} ${n === 1 ? 'ends' : 'end'} this week` : '');

// ---- sections ----

export type SectionId = 'l1_trials' | 'l2_trials' | 'leads_joined' | 'converted';

export const sectionsFor = (level: RepLevel | null | undefined): SectionId[] => {
  if (level === 'L1') return ['l1_trials', 'leads_joined', 'converted'];
  if (level === 'L2') return ['l2_trials', 'leads_joined', 'converted'];
  if (level === 'L3') return ['converted', 'leads_joined'];
  return [];
};

export interface SectionMeta {
  title: string;
  sub: string;
  empty: string;
}

export const sectionMeta = (id: SectionId): SectionMeta => {
  switch (id) {
    case 'l1_trials':
      return {
        title: 'Trials I booked',
        sub: 'booked by me, or by the student within 7 days of my task. Did they turn up?',
        empty: 'No trials booked by you in this period.'
      };
    case 'l2_trials':
      return {
        title: 'My trials joined',
        sub: 'trials that started in the period that I covered with a reminder or join task, and whether the student joined',
        empty: 'No trials covered by you in this period.'
      };
    case 'leads_joined':
      return {
        title: 'Leads I joined',
        sub: 'students who became mine as advisor in the period; their status as of now',
        empty: 'No leads joined in this period.'
      };
    default:
      return {
        title: 'Joined & converted',
        sub: 'leads I joined in the period who have become members so far',
        empty: 'No leads joined in this period have converted yet.'
      };
  }
};

export const sectionCount = (d: MyLeadsData, id: SectionId): string => {
  switch (id) {
    case 'l1_trials': {
      const { l1 } = d;
      if (!l1) return '';
      const review = l1.unknown ? ` · ${l1.unknown} under review` : '';
      return `${l1.booked} booked · ${l1.attended} attended · ${l1.no_show} no-show · ${l1.cancelled} cancelled · ${l1.upcoming} upcoming${review}`;
    }
    case 'l2_trials': {
      const { l2 } = d;
      if (!l2) return '';
      const past = l2.past ?? 0;
      return joinHint(
        `${l2.covered} covered`,
        `${l2.joined} of ${past} joined`,
        past > 0 ? pct(l2.joined / past) : '',
        l2.upcoming ? `${l2.upcoming} upcoming` : ''
      );
    }
    case 'leads_joined': {
      const j = d.leads_joined;
      if (!j) return '';
      return joinHint(
        plural(j.count, 'lead', 'leads'),
        `${j.open ?? 0} open`,
        `${j.paid ?? 0} paid`,
        `${j.window_ended ?? 0} window ended`,
        ending(j.ending_this_week)
      );
    }
    default: {
      const c = d.converted;
      if (!c) return '';
      return joinHint(
        plural(c.count, 'member', 'members'),
        c.three_months_plus > 0 ? `${c.three_months_plus} on 3 months+` : ''
      );
    }
  }
};

// ---- My shift today ----

export type ScoreTone = 'success' | 'error' | 'warning' | 'info' | 'neutral';

export interface ShiftTile {
  key: string;
  label: string;
  value: string;
  detail: string;
  tone: ScoreTone;
}

// One table cell: red when bad, with an optional muted "+k waiting".
export interface ScoreCell {
  text: string;
  bad?: boolean;
  waiting?: string;
}

export interface ScoreRow {
  // "11:00–12:00"; phones show the start only.
  label: string;
  current: boolean;
  future: boolean;
  cells: ScoreCell[];
}

export interface ScoreColumn {
  label: string;
  // Short header for phones.
  short: string;
}

export interface ScoreTable {
  key: string;
  title: string;
  badge: { text: string; tone: 'success' | 'error' };
  columns: ScoreColumn[];
  rows: ScoreRow[];
  total: ScoreCell[];
}

export interface ShiftViewModel {
  mode: 'stats' | 'message';
  message: string;
  sub: string;
  tiles: ShiftTile[];
  tables: ScoreTable[];
  footer: string;
}

// "Priya Shah" -> "Priya"; '' or missing -> "you".
export const doneByLabel = (repName?: string): string => {
  const first = (repName || '').trim().split(/\s+/)[0];
  return first || 'you';
};

// "11:00" -> "11:00–12:00" (wraps past midnight).
// endClock caps the last bucket at the shift end (a 17:00-17:45 tail, not 18:00) when it falls inside the hour.
export const hourRangeLabel = (label: string, endClock?: string | null): string => {
  const [hh, mm] = label.split(':').map(Number);
  if (Number.isNaN(hh)) return label;
  const startMin = hh * 60 + (mm || 0);
  let next = `${String((hh + 1) % 24).padStart(2, '0')}:${String(mm || 0).padStart(2, '0')}`;
  const [eh, em] = (endClock || '').split(':').map(Number);
  if (endClock && !Number.isNaN(eh)) {
    const span = (eh * 60 + (em || 0) - startMin + 1440) % 1440;
    if (span > 0 && span < 60) next = endClock;
  }
  return `${label}\u2013${next}`;
};

const SHIFT_MESSAGES: Record<string, string> = {
  not_today: 'Not on shift today (weekly off).',
  leave: 'On leave today.',
  no_shift: 'No shift hours set. Ask your manager to set them in Sales users.'
};

// hour_start_ist may be "HH:MM" or a full timestamp.
const hourParts = (raw: string): { label: string; hour: number } | null => {
  const clock = /^(\d{1,2}):(\d{2})/.exec(raw || '');
  if (clock) return { label: `${clock[1].padStart(2, '0')}:${clock[2]}`, hour: Number(clock[1]) };
  const t = toMs(raw);
  if (Number.isNaN(t)) return null;
  const local = new Date(t + IST_OFFSET_MS);
  const hh = local.getUTCHours();
  const mm = local.getUTCMinutes();
  return { label: `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`, hour: hh };
};

// Shift start/end: "HH:MM" or a full timestamp -> "10:00 PM" (IST); '' when unreadable.
export const shiftClockLabel = (raw: string | null | undefined): string => {
  const p = hourParts(raw || '');
  if (!p) return '';
  const [hh, mm] = p.label.split(':').map(Number);
  if (!(hh >= 0 && hh < 24 && mm >= 0 && mm < 60)) return '';
  return `${hh % 12 || 12}:${String(mm).padStart(2, '0')} ${hh < 12 ? 'AM' : 'PM'}`;
};

export const SHIFT_UNAVAILABLE = 'Shift data is not available.';
export const SCORECARD_UNAVAILABLE = 'Scorecard not available yet.';

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December'
];

// "21m", "1h 5m", "0m".
export const talkTime = (seconds: number): string => {
  const m = Math.round(Math.max(0, seconds) / 60);
  return m >= 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m}m`;
};

// Whole-number percent; '—' when there is nothing to divide by.
export const pctText = (num: number, den: number): string =>
  den > 0 ? `${Math.round((num / den) * 100)}%` : '\u2014';

const missedCell = (n: number, waiting: number): ScoreCell => ({
  text: String(n),
  bad: n > 0,
  waiting: waiting > 0 ? `+${waiting} waiting` : undefined
});

interface HourLike {
  hour_start_ist: string;
  ended: boolean;
}

// Rows in order with their state: the running hour is the first not yet ended while on shift.
const hourStates = <H extends HourLike>(hours: H[], on: boolean, endIst: string | null) => {
  const parsed = hours
    .map((h) => ({ h, p: hourParts(h.hour_start_ist) }))
    .filter((x): x is { h: H; p: { label: string; hour: number } } => !!x.p);
  const runningIdx = on ? parsed.findIndex(({ h }) => !h.ended) : -1;
  return parsed.map(({ h, p }, i) => ({
    h,
    label: hourRangeLabel(p.label, i === parsed.length - 1 ? hourParts(endIst || '')?.label : null),
    current: i === runningIdx,
    future: !h.ended && i !== runningIdx
  }));
};

const sumOf = <H>(rows: H[], pick: (h: H) => number): number =>
  rows.reduce((a, h) => a + pick(h), 0);

const badge = (num: number, den: number, good: number, suffix: string) => ({
  text: `${pctText(num, den)} ${suffix}`,
  tone: (den > 0 && num / den >= good ? 'success' : 'error') as 'success' | 'error'
});

export const shiftView = (
  shift: LeadsShift | null | undefined,
  scorecard: Scorecard | null | undefined,
  level: RepLevel,
  now: number,
  // Team's Leads passes the viewed rep's name; My Leads leaves it out ("you").
  repName?: string
): ShiftViewModel => {
  const empty: ShiftViewModel = {
    mode: 'message',
    message: '',
    sub: '',
    tiles: [],
    tables: [],
    footer: ''
  };
  if (!shift) return { ...empty, message: SHIFT_UNAVAILABLE };
  const startLabel = shiftClockLabel(shift.start_ist);
  const endLabel = shiftClockLabel(shift.end_ist);
  const window = startLabel && endLabel ? `${startLabel} to ${endLabel} IST` : '';
  if (SHIFT_MESSAGES[shift.state])
    return { ...empty, sub: window, message: SHIFT_MESSAGES[shift.state] };
  const sub = joinHint(window, 'Today', shift.state === 'off' ? 'off shift now' : '');
  const on = shift.state === 'on';
  const who = doneByLabel(repName);
  const you = who === 'you' ? 'you' : who;
  const your = who === 'you' ? 'your' : `${who}\u2019s`;
  const me = who === 'you' ? 'me' : who;
  const month = MONTH_NAMES[new Date(now + IST_OFFSET_MS).getUTCMonth()];

  const calls = level !== 'L3' ? scorecard?.calls : null;
  const conv = level === 'L3' ? scorecard?.conversions : null;
  if (!calls && !conv) return { ...empty, sub, message: SCORECARD_UNAVAILABLE };

  if (calls) {
    const w = calls.window_minutes;
    const rows = hourStates(calls.hours || [], on, shift.end_ist);
    const live = rows.filter((r) => !r.future).map((r) => r.h);
    const n = sumOf(live, (h) => h.new_tasks);
    const called = sumOf(live, (h) => h.called_in_window);
    const connected = sumOf(live, (h) => h.connected_calls);
    const missW = sumOf(live, (h) => h.missed_window);
    const waitW = sumOf(live, (h) => h.missed_window_waiting);
    const miss30 = sumOf(live, (h) => h.missed_30);
    const wait30 = sumOf(live, (h) => h.missed_30_waiting);
    const booked = calls.session_kind === 'booked';
    const sessionLabel = booked ? 'Sessions booked' : 'Sessions joined';
    const inside = (k: number) => (k > 0 ? ` \u00b7 ${k} still inside the window` : '');
    const tiles: ShiftTile[] = [
      {
        key: 'sessions',
        label: sessionLabel,
        value: String(sumOf(live, (h) => h.sessions)),
        detail: booked
          ? `trials ${you} booked from Hridaya for students on today\u2019s tasks`
          : `trial students who joined from ${your} L2 tasks today`,
        tone: 'success'
      },
      {
        key: 'missed_window',
        label: `Missed calling in ${w} mins`,
        value: String(missW),
        detail: `of ${n} new tasks, no call attempt within ${w} min${inside(waitW)}`,
        tone: 'error'
      },
      {
        key: 'missed_30',
        label: 'Missed calling in 30 mins',
        value: String(miss30),
        detail: `of ${n} new tasks, no call attempt within 30 min${inside(wait30)}`,
        tone: 'warning'
      }
    ];
    const blank = (k: number): ScoreCell[] => Array.from({ length: k }, () => ({ text: '' }));
    const speed: ScoreTable = {
      key: 'speed',
      title: 'Speed to call',
      badge: badge(called, n, 0.7, `called within ${w} min`),
      columns: [
        { label: 'New tasks', short: 'New' },
        { label: `Claimed by ${me}`, short: 'Claimed' },
        { label: `Called within ${w} mins`, short: `Called \u2264${w}m` },
        { label: `Missed in ${w} mins`, short: `Missed ${w}m` },
        { label: 'Missed in 30 mins', short: 'Missed 30m' }
      ],
      rows: rows.map((r) => ({
        label: r.label,
        current: r.current,
        future: r.future,
        cells: r.future
          ? blank(5)
          : [
              { text: String(r.h.new_tasks) },
              { text: String(r.h.claimed_by_me) },
              { text: String(r.h.called_in_window) },
              missedCell(r.h.missed_window, r.h.missed_window_waiting),
              missedCell(r.h.missed_30, r.h.missed_30_waiting)
            ]
      })),
      total: [
        { text: String(n) },
        { text: String(sumOf(live, (h) => h.claimed_by_me)) },
        { text: String(called) },
        missedCell(missW, waitW),
        missedCell(miss30, wait30)
      ]
    };
    const results: ScoreTable = {
      key: 'results',
      title: 'What the calls produced',
      badge: badge(connected, called, 0.5, 'connected'),
      columns: [
        { label: 'Connected calls', short: 'Connected' },
        { label: 'Talk time', short: 'Talk' },
        { label: sessionLabel, short: booked ? 'Booked' : 'Joined' },
        { label: 'Members', short: 'Members' }
      ],
      rows: rows.map((r) => ({
        label: r.label,
        current: r.current,
        future: r.future,
        cells: r.future
          ? blank(4)
          : [
              { text: String(r.h.connected_calls) },
              { text: talkTime(r.h.talk_seconds) },
              { text: String(r.h.sessions) },
              { text: String(r.h.members) }
            ]
      })),
      total: [
        { text: String(connected) },
        { text: talkTime(sumOf(live, (h) => h.talk_seconds)) },
        { text: String(sumOf(live, (h) => h.sessions)) },
        { text: String(sumOf(live, (h) => h.members)) }
      ]
    };
    return {
      mode: 'stats',
      message: '',
      sub,
      tiles,
      tables: [speed, results],
      footer: `Members this month from ${your} work: ${calls.members_month} (first purchases by students ${you} worked in ${month}). The table counts those that happened in each hour today.`
    };
  }

  const c = conv as NonNullable<typeof conv>;
  const rows = hourStates(c.hours || [], on, shift.end_ist);
  const live = rows.filter((r) => !r.future).map((r) => r.h);
  const fin = sumOf(live, (h) => h.sessions_finished);
  const joined = sumOf(live, (h) => h.sessions_joined);
  const gen = sumOf(live, (h) => h.priority_generated);
  const done = sumOf(live, (h) => h.priority_completed);
  const blank = (k: number): ScoreCell[] => Array.from({ length: k }, () => ({ text: '' }));
  const tiles: ShiftTile[] = [
    {
      key: 'converted_today',
      label: 'Converted today',
      value: String(c.converted_today),
      detail: `first purchases from sessions ${you} joined today`,
      tone: 'success'
    },
    {
      key: 'converted_month',
      label: 'Converted this month',
      value: String(c.converted_month),
      detail: `first purchases from sessions ${you} joined in ${month}`,
      tone: 'info'
    },
    {
      key: 'conversion',
      label: '% conversion',
      value: pctText(c.converted_month, c.joined_month),
      detail: `${c.converted_month} of ${c.joined_month} sessions ${you} joined this month`,
      tone: 'neutral'
    }
  ];
  const sessions: ScoreTable = {
    key: 'sessions',
    title: 'Sessions',
    badge: badge(joined, fin, 0.5, `${you} joined`),
    columns: [
      { label: 'Sessions finished', short: 'Finished' },
      { label: `Joined by ${me}`, short: 'Joined' },
      { label: 'Share joined', short: 'Share' }
    ],
    rows: rows.map((r) => ({
      label: r.label,
      current: r.current,
      future: r.future,
      cells: r.future
        ? blank(3)
        : [
            { text: String(r.h.sessions_finished) },
            { text: String(r.h.sessions_joined) },
            { text: pctText(r.h.sessions_joined, r.h.sessions_finished) }
          ]
    })),
    total: [{ text: String(fin) }, { text: String(joined) }, { text: pctText(joined, fin) }]
  };
  const prio: ScoreTable = {
    key: 'priority',
    title: 'Priority tasks',
    badge: badge(done, gen, 0.8, 'completed'),
    columns: [
      { label: 'Priority tasks generated', short: 'Generated' },
      { label: 'Completed', short: 'Done' },
      { label: '% completion', short: '%' },
      { label: 'Not completed within 90 mins', short: 'Not in 90m' }
    ],
    rows: rows.map((r) => ({
      label: r.label,
      current: r.current,
      future: r.future,
      cells: r.future
        ? blank(4)
        : [
            { text: String(r.h.priority_generated) },
            { text: String(r.h.priority_completed) },
            { text: pctText(r.h.priority_completed, r.h.priority_generated) },
            missedCell(r.h.not_done_90, r.h.not_done_90_waiting)
          ]
    })),
    total: [
      { text: String(gen) },
      { text: String(done) },
      { text: pctText(done, gen) },
      missedCell(
        sumOf(live, (h) => h.not_done_90),
        sumOf(live, (h) => h.not_done_90_waiting)
      )
    ]
  };
  return {
    mode: 'stats',
    message: '',
    sub,
    tiles,
    tables: [sessions, prio],
    footer: `Not done in 90m counts all ${your} tasks, instant and others, whose 90 minutes have passed.`
  };
};

// "▼ 22" (fewer overdue, good) / "▲ 4" (more overdue) / '' for no change or nothing to compare.
export const overdueDelta = (
  d: number | null
): { text: string; tone: 'success' | 'error' | '' } => {
  if (d === null || d === 0) return { text: '', tone: '' };
  return d < 0 ? { text: `\u25bc ${-d}`, tone: 'success' } : { text: `\u25b2 ${d}`, tone: 'error' };
};

// ---- help ----

export const helpBullets = (level: RepLevel, startIst?: string, endIst?: string): string[] => {
  const range = periodRange(startIst, endIst);
  const between = range ? `between ${range}` : 'in the period';
  const byLevel: Record<RepLevel, string[]> = {
    L1: [
      `Trials I booked: trials booked ${between}, by you or by the student within 7 days after your last done task for them ("After my task"). Each trial has one owner; statuses are as of now.`
    ],
    L2: [
      `My trials joined: trials that started ${between} where you completed a reminder or join task. Joined means the student attended.`
    ],
    L3: []
  };
  return [
    ...byLevel[level],
    `Leads I joined: leads you joined ${between}; their status as of now (open with days left, paid, or window ended).`,
    'Joined & converted: those leads whose first membership purchase came while you were their advisor, at any time up to now. Fully refunded purchases and family seats do not count.',
    'Last touch: your latest call, SMS or WhatsApp, done task or note. Chat is not counted.',
    level === 'L3'
      ? 'My shift today (L3): Converted today and this month are first membership purchases from trial sessions you joined; % conversion divides this month\u2019s by the sessions you joined this month. By hour: sessions finished, the ones you joined, your priority (instant) tasks and how many you completed, and your tasks not done within 90 minutes.'
      : `My shift today (${level}): Missed calling counts new tasks at your level with no call attempt (any outcome, by anyone) within ${level === 'L2' ? 15 : 10} or 30 minutes of creation; tasks still inside that time show as waiting. By hour: new tasks, the ones you claimed, calls made in time, your connected calls and talk time, ${level === 'L2' ? 'trial students who joined from your tasks' : 'trials you booked from Hridaya'}, and members (first purchases by students you worked this month).`,
    'The page refreshes itself every 5 minutes. Work your tasks from the Tasks tab.'
  ];
};

// ---- Team's Leads view ----

export interface RepOption {
  value: string;
  label: string;
  // null = no rep level (manager / super user); grouped under "Other".
  level: RepLevel | null;
}

export interface RepGroup {
  label: string;
  options: RepOption[];
}

// Never blank: name, else the email's local part, else "Unnamed (<uuid prefix>)" (matches goserver).
export const repDisplayName = (m: Pick<TeamMember, 'name' | 'email' | 'ops_uuid'>): string => {
  const name = (m.name || '').trim();
  if (name) return name;
  const local = (m.email || '').trim().split('@')[0];
  return local || `Unnamed (${(m.ops_uuid || '').slice(0, 8)})`;
};

const byLabel = (a: RepOption, b: RepOption) =>
  a.label.localeCompare(b.label, undefined, { sensitivity: 'base' });

// Active L1-L3 reps only: the server answers target_not_rep for anyone else.
export const teamRosterOptions = (members: TeamMember[]): RepOption[] =>
  members
    .filter((m) => m.status === 'ACTIVE' && !!m.level && (LEVELS as string[]).includes(m.level))
    .map((m) => ({ value: m.ops_uuid, label: repDisplayName(m), level: m.level as RepLevel }))
    .sort(byLabel);

// L1, L2, L3 groups (names A-Z, level in the group label), then "Other"; empty groups dropped.
export const groupRepOptions = (options: RepOption[]): RepGroup[] => {
  const groups: RepGroup[] = [
    ...LEVELS.map((lvl) => ({ label: lvl, options: options.filter((o) => o.level === lvl) })),
    {
      label: 'Other',
      options: options.filter((o) => !o.level || !(LEVELS as string[]).includes(o.level))
    }
  ];
  return groups
    .map((g) => ({ ...g, options: [...g.options].sort(byLabel) }))
    .filter((g) => g.options.length > 0);
};

export const REP_PARAM = 'rep';

export const NOT_ON_ROSTER = "You're not on the sales roster yet — ask your manager.";

export const repFromSearch = (search: string): string =>
  new URLSearchParams(search || '').get(REP_PARAM) || '';

export const searchWithRep = (search: string, opsUuid: string): string => {
  const params = new URLSearchParams(search || '');
  if (opsUuid) params.set(REP_PARAM, opsUuid);
  else params.delete(REP_PARAM);
  const out = params.toString();
  return out ? `?${out}` : '';
};

const REASONS: Record<string, string> = {
  not_manager: 'Only sales managers and super users can view another rep.',
  target_not_rep: 'This person is not an active L1, L2 or L3 rep.',
  bad_period: 'That period is not supported.',
  my_leads_timeout: 'Taking longer than usual — retrying'
};

// Server build timed out; transient, so polling carries on.
export const isLeadsTimeout = (status: number | undefined, reason: string): boolean =>
  status === 503 && reason === 'my_leads_timeout';

export const leadsErrorMessage = (status: number | undefined, reason: string): string => {
  if (reason && REASONS[reason]) return REASONS[reason];
  if (status === 404) return 'That team member was not found.';
  if (status === 403) return REASONS.not_manager;
  return reason ? `Could not load leads: ${reason}` : 'Could not load leads.';
};

// ---- Team's Leads: today by level ----

export interface LevelRowView {
  level: RepLevel;
  // "11:00 AM to 7:00 PM IST · 4 reps on shift"; "No shifts today" when nobody at the level has one.
  caption: string;
  noShift: boolean;
  openAtStart: number;
  cameIn: number;
  done: number;
  overdue: number;
  change: { text: string; tone: 'success' | 'error' | '' };
  notDue: number;
  backlog: number;
}

export const LEVEL_NO_SHIFT = 'No shifts today';

export const levelRowView = (r: LevelShiftSummary): LevelRowView => {
  const noShift = r.state === 'no_shift';
  const s = shiftClockLabel(r.start_ist);
  const e = shiftClockLabel(r.end_ist);
  const caption = noShift
    ? LEVEL_NO_SHIFT
    : joinHint(
        s && e ? `${s} to ${e} IST` : '',
        `${plural(r.reps_on_shift, 'rep', 'reps')} on shift`
      );
  return {
    level: r.level,
    caption,
    noShift,
    openAtStart: r.carried_in,
    cameIn: r.came_in,
    done: r.done,
    overdue: r.past_due,
    change: noShift ? { text: '', tone: '' } : overdueDelta(r.past_due - r.past_due_at_start),
    notDue: r.left_not_due,
    backlog: r.older_backlog
  };
};

// Rows in L1, L2, L3 order whatever order the server sends; unknown levels dropped.
export const levelRowsView = (levels: LevelShiftSummary[]): LevelRowView[] =>
  LEVELS.map((lvl) => levels.find((r) => r.level === lvl))
    .filter((r): r is LevelShiftSummary => !!r)
    .map(levelRowView);

// The rep picker narrowed to one level ('' keeps everyone).
// The rep already shown stays listed, so the picker never shows a blank for the open page.
export const optionsForLevel = (
  options: RepOption[],
  level: RepLevel | '',
  keep = ''
): RepOption[] =>
  level ? options.filter((o) => o.level === level || (!!keep && o.value === keep)) : options;
